import { BOOLEAN_OPERATIONS } from '@/adapters/primitive/_data/booleanOperations';
import {
    buildBooleanPlaceholderWheel,
    buildPrimitiveOperationDraftWheels,
    dimStableWheelRoles,
    toBooleanAlgebraWheelPosition,
    toWheelLayout,
} from '@/adapters/primitive/_pure/derivation/wheels';
import { planBooleanPathAttempts } from '@/adapters/primitive/_pure/derivation/boolean/algebra';
import { rotateBooleanSection, rotateBooleanSectionLateral, toBooleanOperationJunction, toBooleanSegmentEndpoint } from '@/adapters/primitive/_pure/derivation/boolean/geometry';
import { toBooleanChannelSegmentKey, toBooleanOperandSegmentKey, toBooleanTravellerSegmentKey } from '@/adapters/primitive/_pure/derivation/keyCodec';
import { momentKey, stepMoment } from '@/adapters/primitive/_pure/derivation/primitiveMoment';
import { selectPrimitiveDirectionEffects } from '@/adapters/primitive/_pure/derivation/primitiveDirectionRules';
import type { DirectionCatalog } from '@/_contract/directionRule';
import type { PrimitiveOperationDirectionEffect } from '@/adapters/primitive/_contract/_~generated/operationDirectionEffect';
import type { BooleanAlgebraOperation } from '@/adapters/primitive/_contract/boolean/operation';
import type { BooleanRodSegment, BooleanRodState } from '@/adapters/primitive/_contract/boolean/rod';
import type { Config, PrimitiveWheelRole, PrimitiveWheelState } from '@/adapters/primitive/_contract/config';
import type { PrimitiveOperationInputDraft } from '@/adapters/primitive/_contract/operation';
import type { PrimitiveStepState } from '@/adapters/primitive/_contract/steps';
import type { BooleanOperationTrace } from '@/adapters/primitive/_contract/trace';

type Position = [number, number, number];

export function toBooleanWheelPositions(config: Config, operation: BooleanAlgebraOperation): {
    inputOne: Position;
    inputTwo: Position;
    output: Position;
} {
    const layout = toWheelLayout({ config, operandSchedulingMode: config.natural.operandSchedulingMode });
    return {
        inputOne: toBooleanAlgebraWheelPosition({ config, layout, role: 'boolean-input-one', operation }),
        inputTwo: toBooleanAlgebraWheelPosition({ config, layout, role: 'boolean-input-two', operation }),
        output: toBooleanAlgebraWheelPosition({ config, layout, role: 'boolean-output', operation }),
    };
}

function booleanWheel(role: PrimitiveWheelRole, value: boolean, position: Position, opacity = 1): PrimitiveWheelState {
    return {
        role,
        valueKind: 'boolean',
        value,
        powers: [0],
        position,
        bandOpacityByPower: { 0: opacity },
    };
}

type InputRole = 'boolean-input-one' | 'boolean-input-two';
type RodTraveller = { role: InputRole; pathKey: string; from: Position; to: Position; lateral: Position; opacity: number };

/** The operation declares its Result paths; operands only acquire their colour when their wheels arrive. */
export function buildBooleanRodState(args: {
    config: Config;
    operation: BooleanAlgebraOperation;
    positions?: ReturnType<typeof toBooleanWheelPositions>;
    inputOne?: boolean;
    inputTwo?: boolean;
    activePathKey?: string | null;
    departedRoles?: readonly InputRole[];
    departedByPath?: Readonly<Record<string, readonly InputRole[]>>;
    travellers?: readonly RodTraveller[];
}): BooleanRodState {
    const { config, operation } = args;
    const positions = args.positions ?? toBooleanWheelPositions(config, operation);
    const paths = BOOLEAN_OPERATIONS[operation].paths;
    const colorFor = (value: boolean) => value
        ? config.base.display.cell.countedColor
        : config.base.display.cell.uncountedColor;
    const neutral = config.base.display.placeholder.outlineColor;
    const segments: BooleanRodSegment[] = [];
    paths.forEach((path, index) => {
        const junction = toBooleanOperationJunction({
            inputOne: positions.inputOne,
            ...(operation === 'logical-not' ? {} : { inputTwo: positions.inputTwo }),
            output: positions.output,
            wheelGap: config.boolean.display.track.wheelEndGap,
            height: paths.length === 1 ? 0 : (index === 0 ? 1 : -1) * config.boolean.display.layout.branchHeight,
        });
        const output = toBooleanSegmentEndpoint(positions.output, junction, config.boolean.display.track.wheelEndGap);
        const mid: Position = [
            (junction[0] + output[0]) / 2,
            (junction[1] + output[1]) / 2,
            (junction[2] + output[2]) / 2,
        ];
        const add = (segmentKey: string, from: Position, to: Position, color: string, multiplier = 1) => {
            segments.push({ segmentKey: `${path.pathKey}:${segmentKey}`, from, to, color, opacity: multiplier });
        };
        const addTrack = (section: 'output-near' | 'output-far' | 'output', from: Position, to: Position, color: string) => {
            add(section, from, to, color, 0);
            segments.push({
                segmentKey: toBooleanChannelSegmentKey(path.pathKey, section), from, to, color, opacity: 1,
                profile: {
                    profileKind: 'channel', lateral: [1, 0, 0],
                    innerWidth: config.boolean.display.track.halfGap * 2,
                    lipThickness: config.boolean.display.track.lipThickness,
                    lipHeight: config.boolean.display.track.lipHeight,
                    floorDepth: config.boolean.display.track.floorDepth,
                    floorColor: config.boolean.display.track.floorColor,
                    border: { ...config.boolean.display.decisiveOutline, opacity: 0 },
                },
            });
        };
        const addOperand = (role: InputRole, position: Position, value: boolean | undefined, opacity: number) => {
            const segmentKey = toBooleanOperandSegmentKey(path.pathKey, role);
            if (value === undefined) {
                segments.push({ segmentKey, from: position, to: junction, color: neutral, opacity });
                return;
            }
            const color = colorFor(value);
            const start = toBooleanSegmentEndpoint(position, junction, config.boolean.display.track.wheelEndGap);
            const lateral = rotateBooleanSectionLateral({
                pivot: junction, from: operation === 'logical-not' ? output : mid,
                to: start, progress: 1, lateral: [1, 0, 0],
            });
            // Reverse both length and width axes to share the emitted traveller's normal.
            segments.push({
                segmentKey, from: start, to: junction, color, opacity,
                profile: {
                    profileKind: 'bar', lateral: [-lateral[0], -lateral[1], -lateral[2]],
                    innerWidth: config.boolean.display.track.halfGap * 2,
                    lipThickness: config.boolean.display.track.lipThickness,
                    lipHeight: config.boolean.display.track.barHeight,
                    floorDepth: config.boolean.display.track.floorDepth,
                },
            });
        };
        addOperand('boolean-input-one', positions.inputOne, args.inputOne,
            (args.departedByPath?.[path.pathKey]?.includes('boolean-input-one')
                || args.departedRoles?.includes('boolean-input-one') && args.activePathKey === path.pathKey) ? config.natural.display.band.spentOpacity : 1);
        if (operation !== 'logical-not') {
            addOperand('boolean-input-two', positions.inputTwo, args.inputTwo,
                (args.departedByPath?.[path.pathKey]?.includes('boolean-input-two')
                    || args.departedRoles?.includes('boolean-input-two') && args.activePathKey === path.pathKey) ? config.natural.display.band.spentOpacity : 1);
            addTrack('output-far', junction, mid, colorFor(path.requiredInputs[0]));
            addTrack('output-near', mid, output, colorFor(path.requiredInputs[1]!));
            const { halfWidth, color } = config.boolean.display.splitMarker;
            add('output-joint', [mid[0] - halfWidth, mid[1], mid[2]], [mid[0] + halfWidth, mid[1], mid[2]], color);
        } else {
            addTrack('output', junction, output, colorFor(path.requiredInputs[0]));
        }
    });
    for (const traveller of args.travellers ?? []) {
        segments.push({
            segmentKey: toBooleanTravellerSegmentKey(traveller.pathKey, traveller.role),
            from: traveller.from,
            to: traveller.to,
            color: colorFor(traveller.role === 'boolean-input-one' ? args.inputOne! : args.inputTwo!),
            opacity: traveller.opacity,
            profile: {
                profileKind: 'bar', lateral: traveller.lateral,
                innerWidth: config.boolean.display.track.halfGap * 2,
                lipThickness: config.boolean.display.track.lipThickness,
                lipHeight: config.boolean.display.track.barHeight,
                floorDepth: config.boolean.display.track.floorDepth,
            },
        });
    }
    return { segments };
}

export function buildBooleanDraftRod(config: Config, draft: PrimitiveOperationInputDraft): BooleanRodState | undefined {
    if (draft.operation === 'logical-not') return buildBooleanRodState({
        config, operation: draft.operation,
        ...(draft.inputText === '' ? {} : { inputOne: draft.inputText === 'true' }),
    });
    if (draft.operation === 'logical-and' || draft.operation === 'logical-nand'
        || draft.operation === 'logical-xor' || draft.operation === 'logical-xnor'
        || draft.operation === 'logical-or' || draft.operation === 'logical-nor') {
        return buildBooleanRodState({
            config, operation: draft.operation,
            ...(draft.operandOneText === '' ? {} : { inputOne: draft.operandOneText === 'true' }),
            ...(draft.operandTwoText === '' ? {} : { inputTwo: draft.operandTwoText === 'true' }),
        });
    }
    return undefined;
}

export function buildBooleanAssignedWheels(config: Config, draft: PrimitiveOperationInputDraft): PrimitiveWheelState[] {
    return buildPrimitiveOperationDraftWheels({ config, draft });
}

export function deriveBooleanAssignmentStep(args: {
    config: Config;
    previousDraft: PrimitiveOperationInputDraft;
    nextDraft: PrimitiveOperationInputDraft;
    inputRole: 'boolean-input-one' | 'boolean-input-two';
}): PrimitiveStepState {
    const { config, previousDraft, nextDraft, inputRole } = args;
    const before = buildBooleanAssignedWheels(config, previousDraft);
    const after = buildBooleanAssignedWheels(config, nextDraft);
    const beforeRod = buildBooleanDraftRod(config, previousDraft)!;
    const afterRod = buildBooleanDraftRod(config, nextDraft)!;
    const incoming = after.find((wheel) => wheel.role === inputRole);
    const filled = after.map((wheel) => wheel.role === 'boolean-output'
        ? before.find((previous) => previous.role === 'boolean-output')!
        : wheel);
    const arriving = incoming?.valueKind === 'boolean'
        ? filled.map((wheel) => wheel.role === inputRole ? { ...wheel, bandOpacityByPower: { 0: 0 } } : wheel)
        : filled;
    const assignmentDuration = { durationMs: config.boolean.animation.arrivalDurationMs };
    const growingRod: BooleanRodState = {
        segments: afterRod.segments.map((segment) => {
            const previous = beforeRod.segments.find((candidate) => candidate.segmentKey === segment.segmentKey);
            if (segment.profile?.profileKind !== 'bar' || previous?.profile !== undefined) return segment;
            const thin = config.boolean.display.track.lipThickness * 0.02;
            return {
                ...segment,
                profile: { ...segment.profile, innerWidth: thin, lipHeight: thin },
            };
        }),
    };
    const keyframes: PrimitiveStepState['keyframes'][number][] = [
        { wheels: arriving, booleanRod: beforeRod, transition: 'instant', events: [] },
        { wheels: filled, booleanRod: beforeRod, transition: assignmentDuration, events: [] },
        { wheels: filled, booleanRod: growingRod, transition: 'instant', events: [] },
        { wheels: filled, booleanRod: afterRod, transition: assignmentDuration, events: [] },
    ];
    return {
        ...momentKey(stepMoment('assign', { role: inputRole })), label: 'Assign Input',
        description: `Assign ${inputRole === 'boolean-input-one' ? 'Operand One' : 'Operand Two'}.`,
        keyframes,
    };
}

export function deriveBooleanAlgebraStepStates(args: {
    config: Config;
    operation: BooleanAlgebraOperation;
    inputOne: boolean;
    inputTwo?: boolean;
}): PrimitiveStepState[] {
    return projectBooleanOperationTrace({ ...args, trace: planBooleanPathAttempts({
        operation: args.operation,
        inputs: args.operation === 'logical-not' ? [args.inputOne] : [args.inputOne, args.inputTwo!],
    }) });
}

export function projectBooleanOperationTrace(args: {
    config: Config;
    operation: BooleanAlgebraOperation;
    inputOne: boolean;
    inputTwo?: boolean;
    trace: BooleanOperationTrace;
    directionCatalog?: DirectionCatalog<PrimitiveOperationDirectionEffect>;
}): PrimitiveStepState[] {
    const { config, operation, inputOne, inputTwo } = args;
    const positions = toBooleanWheelPositions(config, operation);
    const unary = operation === 'logical-not';
    const plan = args.trace;
    const outputPlaceholder = buildBooleanPlaceholderWheel({
        layout: toWheelLayout({ config, operandSchedulingMode: config.natural.operandSchedulingMode }),
        role: 'boolean-output',
    });
    const inputOneWheel = booleanWheel('boolean-input-one', inputOne, positions.inputOne);
    const inputTwoWheel = booleanWheel('boolean-input-two', inputTwo ?? false, positions.inputTwo);
    const wheels = (output?: boolean, outputOpacity = 1): PrimitiveWheelState[] => [
        inputOneWheel,
        ...(unary ? [] : [inputTwoWheel]),
        output === undefined ? outputPlaceholder
            : booleanWheel('boolean-output', output, positions.output, outputOpacity),
    ];
    const departedByPath: Record<string, readonly InputRole[]> = {};
    const parked: RodTraveller[] = [];
    const rod = (selectedPath?: string | null, travellers: readonly RodTraveller[] = parked,
        departed: Readonly<Record<string, readonly InputRole[]>> = departedByPath) => buildBooleanRodState({
        config,
        operation,
        inputOne,
        ...(unary ? {} : { inputTwo: inputTwo! }),
        ...(selectedPath === undefined ? {} : { activePathKey: selectedPath }),
        departedByPath: departed,
        travellers,
    });
    const duration = (durationMs: number) => ({ durationMs });
    const steps: PrimitiveStepState[] = [
        {
            ...momentKey(stepMoment('ready')), label: 'Ready', description: 'Operands are ready to compare; Result is unresolved.',
            keyframes: [{ wheels: wheels(), booleanRod: rod(), transition: 'instant', events: [] }],
        },
    ];
    if (config.boolean.animation.pivotSegments < 1) {
        throw new Error('Boolean track requires a positive pivot segment count.');
    }
    const fractions = Array.from({ length: config.boolean.animation.pivotSegments }, (_, index) => (
        (index + 1) / config.boolean.animation.pivotSegments
    ));
    const turnDuration = duration(config.boolean.animation.pivotDurationMs / fractions.length);
    let successfulPath: string | null = null;
    for (const attempt of plan.attempts) {
        const { pathKey, roleNear, roleFar } = attempt;
        const staticRod = rod(pathKey);
        const startOf = (role: InputRole): Position => [...staticRod.segments.find((segment) =>
            segment.segmentKey === toBooleanOperandSegmentKey(pathKey, role))!.from];
        const outputFar = staticRod.segments.find((segment) => segment.segmentKey === `${pathKey}:${unary ? 'output' : 'output-far'}`)!;
        const outputNear = unary ? null : staticRod.segments.find((segment) => segment.segmentKey === `${pathKey}:output-near`)!;
        const junction = outputFar.from;
        const midpoint = outputFar.to;
        const initialLateral = (start: Position): Position => rotateBooleanSectionLateral({
            pivot: junction, from: midpoint, to: start, progress: 1, lateral: [1, 0, 0],
        });
        const turningLateral = (start: Position, fraction: number): Position => rotateBooleanSectionLateral({
            pivot: junction, from: start, to: midpoint, progress: fraction, lateral: initialLateral(start),
        });
        const traveller = (role: InputRole, from: readonly number[], to: readonly number[], opacity = 1,
            lateral: Position = [1, 0, 0]): RodTraveller => ({
            role, pathKey, from: [...from] as Position, to: [...to] as Position, opacity, lateral,
        });
        const withNear = (destination: readonly number[], from: readonly number[] = junction, opacity = 1,
            lateral: Position = [1, 0, 0]) => rod(
            pathKey, [...parked, traveller(roleNear, from, destination, opacity, lateral)],
            { ...departedByPath, [pathKey]: [roleNear] },
        );
        const nearStart = startOf(roleNear);
        steps.push({
            ...momentKey(stepMoment('approach-near', { path: pathKey })), label: 'Approach Result',
            description: `Bring ${roleNear === 'boolean-input-one' ? 'Operand One' : 'Operand Two'} toward the ${unary ? 'Result' : 'near Result'} track.`,
            keyframes: [
                { wheels: wheels(), booleanRod: rod(pathKey, [...parked, traveller(roleNear, junction, nearStart, 0, initialLateral(nearStart))]), transition: 'instant', events: [] },
                { wheels: wheels(), booleanRod: withNear(nearStart, junction, 1, initialLateral(nearStart)), transition: duration(config.boolean.animation.arrivalDurationMs / 2), events: [] },
                ...fractions.map((fraction) => ({
                    wheels: wheels(),
                    booleanRod: withNear(rotateBooleanSection({ pivot: junction, from: nearStart, to: midpoint, progress: fraction }), junction,
                        1, turningLateral(nearStart, fraction)),
                    transition: turnDuration, events: [],
                })),
            ],
        });
        departedByPath[pathKey] = [roleNear];
        if (unary) {
            parked.push(traveller(roleNear, junction, midpoint));
            if (attempt.fitsNear) successfulPath = pathKey;
            continue;
        }
        steps.push({
            ...momentKey(stepMoment('pivot-near', { path: pathKey })), label: 'Fit Result-near',
            description: attempt.fitsNear ? 'Pivot into the matching near channel.' : 'The near channel does not fit; this path stops.',
            keyframes: [
                { wheels: wheels(), booleanRod: withNear(junction, midpoint), transition: 'instant', events: [] },
                ...fractions.map((fraction) => ({
                    wheels: wheels(),
                    booleanRod: withNear(rotateBooleanSection({ pivot: midpoint, from: junction, to: outputNear!.to, progress: fraction }), midpoint),
                    transition: turnDuration, events: [],
                })),
                ...(attempt.fitsNear
                    ? [{ wheels: wheels(), booleanRod: withNear(outputNear!.to, midpoint, 1), transition: duration(config.boolean.animation.arrivalDurationMs / 2), events: [] }]
                    : []),
            ],
        });
        parked.push(traveller(roleNear, midpoint, outputNear!.to));
        if (!attempt.fitsNear || roleFar === null) continue;

        const farStart = startOf(roleFar);
        const withFar = (destination: readonly number[], opacity = 1) => rod(
            pathKey, [...parked, traveller(roleFar, junction, destination, opacity,
                destination === farStart ? initialLateral(farStart) : [1, 0, 0])],
            { ...departedByPath, [pathKey]: [roleNear, roleFar] },
        );
        steps.push({
            ...momentKey(stepMoment('fit-far', { path: pathKey })), label: 'Fit Result-far',
            description: attempt.fitsFar ? 'Rotate the remaining input section into the far channel.' : 'The far channel does not fit; this path stops.',
            keyframes: [
                { wheels: wheels(), booleanRod: rod(pathKey, [...parked, traveller(roleFar, junction, farStart, 0, initialLateral(farStart))]), transition: 'instant', events: [] },
                { wheels: wheels(), booleanRod: withFar(farStart), transition: duration(config.boolean.animation.arrivalDurationMs / 2), events: [] },
                ...fractions.map((fraction) => ({
                    wheels: wheels(),
                    booleanRod: rod(pathKey, [...parked, traveller(roleFar, junction,
                        rotateBooleanSection({ pivot: junction, from: farStart, to: midpoint, progress: fraction }), 1,
                        turningLateral(farStart, fraction))],
                    { ...departedByPath, [pathKey]: [roleNear, roleFar] }),
                    transition: turnDuration, events: [],
                })),
            ],
        });
        departedByPath[pathKey] = [roleNear, roleFar];
        parked.push(traveller(roleFar, junction, midpoint));
        if (attempt.fitsFar) successfulPath = pathKey;
    }
    const resolvedRod = rod(plan.value ? successfulPath : plan.attempts.at(-1)?.pathKey);
    const decisiveChannels = new Set(plan.value
        ? unary ? [toBooleanChannelSegmentKey(successfulPath!, 'output')]
            : [toBooleanChannelSegmentKey(successfulPath!, 'output-near'), toBooleanChannelSegmentKey(successfulPath!, 'output-far')]
        : plan.attempts.map((attempt) => toBooleanChannelSegmentKey(attempt.pathKey, unary ? 'output'
            : attempt.firstMismatch === 'far' ? 'output-far' : 'output-near')));
    const matchMoment = stepMoment('match-output');
    const outline = selectPrimitiveDirectionEffects({ moment: matchMoment, role: 'decisive-result' }, args.directionCatalog)
        .some(effect => effect.visual === 'boolean-decisive-outline');
    const emphasizedRod: BooleanRodState = {
        segments: resolvedRod.segments.map((segment) => outline && decisiveChannels.has(segment.segmentKey) && segment.profile
            ? { ...segment, profile: { ...segment.profile, border: { ...config.boolean.display.decisiveOutline, opacity: 1 } } }
            : segment),
    };
    const matchOutputStep: PrimitiveStepState = {
        ...momentKey(matchMoment), label: 'Match Result',
        description: plan.value
            ? `The operand sections match ${successfulPath}; Result resolves to true.`
            : 'No accepting path matches; Result resolves to false.',
        keyframes: [
            {
                wheels: wheels(), booleanRod: resolvedRod,
                transition: duration(config.boolean.animation.matchDurationMs / 2), events: [],
            },
            {
                wheels: wheels(plan.value, 0), booleanRod: emphasizedRod, transition: 'instant', events: [],
            },
            {
                wheels: dimStableWheelRoles({
                    config, wheels: wheels(plan.value),
                    roles: unary ? ['boolean-input-one'] : ['boolean-input-one', 'boolean-input-two'],
                }), booleanRod: emphasizedRod,
                transition: duration(config.boolean.animation.matchDurationMs / 2), events: [],
            },
        ],
    };
    steps.push(matchOutputStep);
    return steps;
}